import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { X, Loader2, ChevronDown } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { CATEGORIES, subcategoriesFor } from "@/data/taxonomy";
import { BEACH_CATEGORY } from "@/data/beaches";
import BeachFields, { beachPayload } from "@/components/admin/BeachFields";
import HoursEditor from "@/components/yts/hours/HoursEditor";
import { parseCoords } from "@/lib/coords";
import { useExplore } from "@/lib/ExploreContext";

const TYPES = ["Restaurant", "Experience", "Shop", "Nightlife", "Culture", "Service", "Neighborhood", "Beach"];
const STATUSES = ["Draft", "Published", "Archived"];
const PLANS = ["Free", "Premium"];
const ACTIONS = ["", "Book Experience", "View Menu", "Order Online", "Reserve Table"];
const AFFILIATES = ["", "FareHarbor", "Viator", "OpenTable", "Resy", "Custom Affiliate"];

const inputCls =
  "w-full rounded-[10px] border border-[#deded9] px-3 py-2.5 text-[14px] outline-none focus:border-[#171717] transition-colors disabled:bg-[#f3f2ee] disabled:text-[#9b9a95]";
const labelCls = "text-[10px] tracking-[0.14em] uppercase font-black text-[#6d6d6a] mb-1.5 block";

function Field({ label, hint = null, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[#6d6d6a]">{hint}</div>}
    </div>
  );
}

/**
 * One part of the form, closed until it is wanted. The panel opens as a short
 * index of headings — the part being worked on is the only part on screen — so
 * changing one field never means scrolling past the rest. What is typed lives
 * in the form above, so closing a part loses nothing.
 */
function Section({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#ededed] rounded-[14px] overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full min-h-[52px] flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-[#f7f7f4]"
      >
        <span className="text-[11px] tracking-[0.16em] uppercase font-black text-[#171717]">{title}</span>
        {/* The arrow rides its own filled disc: one obvious tap target, the
            same in both appearances. */}
        <span className="w-7 h-7 shrink-0 grid place-items-center rounded-full bg-[#ffffff] border border-[#ededed] shadow-sm">
          <ChevronDown className={`w-4 h-4 text-[#1c1a17] transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 text-[14px] font-bold text-[#3f3f3c]"
    >
      <span
        className={`w-10 h-6 rounded-full relative transition-colors ${checked ? "bg-[#171717]" : "bg-[#d4d4cf]"}`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
            checked ? "left-[18px]" : "left-0.5"
          }`}
        />
      </span>
      {label}
    </button>
  );
}

/**
 * The single edit screen behind a business, covering every stored field an
 * editor can change: the browse taxonomy (category, then only that category's
 * own subcategories), the neighborhood and coordinates that put it on the map,
 * the descriptions and hours, the photo set, the social and booking links that
 * Premium unlocks, and the plan itself. Basic and Premium are both complete
 * here — the tier is a setting on this form, not a missing half of it.
 */
export default function ListingEditModal({ listing, onClose, onSaved }) {
  const { neighborhoods } = useExplore();
  const [form, setForm] = useState({
    name: listing.name || "",
    type: listing.type || "Restaurant",
    status: listing.status || "Published",
    category: listing.category || "",
    subcategory: listing.subcategory || "",
    neighborhood_id: listing.neighborhood_id || "",
    editorial_description: listing.editorial_description || "",
    description: listing.description || "",
    address: listing.address || "",
    phone: listing.phone || "",
    hours_text: listing.hours_text || "",
    hours_json: listing.hours_json || "",
    website_url: listing.website_url || "",
    language_support: listing.language_support || "",
    price_label: listing.price_label || "",
    amenities: Array.isArray(listing.amenities) ? listing.amenities.join(", ") : listing.amenities || "",
    whatsapp_url: listing.whatsapp_url || "",
    instagram_url: listing.instagram_url || "",
    facebook_url: listing.facebook_url || "",
    tiktok_url: listing.tiktok_url || "",
    twitter_url: listing.twitter_url || "",
    youtube_url: listing.youtube_url || "",
    booking_url: listing.booking_url || "",
    affiliate_provider: listing.affiliate_provider || "",
    primary_action_type: listing.primary_action_type || "",
    primary_action_url: listing.primary_action_url || "",
    storefront_image: listing.storefront_image || "",
    cover_image: listing.cover_image || "",
    gallery_images: listing.gallery_images || [],
    plan: listing.plan || "Free",
    verified: !!listing.verified,
    featured: !!listing.featured,
    editor_pick: !!listing.editor_pick,
    locals_know: !!listing.locals_know,
    beach_area: listing.beach_area || "",
    beach_vibe: listing.beach_vibe || "",
    beach_best_for: listing.beach_best_for || "",
    beach_water: listing.beach_water || "",
    beach_water_level: listing.beach_water_level || "",
    beach_activities: listing.beach_activities || [],
    beach_nearby_tags: listing.beach_nearby_tags || [],
    beach_nearby: listing.beach_nearby || [],
  });
  const [coordsText, setCoordsText] = useState(
    listing.latitude != null && listing.longitude != null ? `${listing.latitude}, ${listing.longitude}` : ""
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setChk = (k) => (v) => setForm({ ...form, [k]: v });

  // A listing keeps its own coordinates as numbers; the box on screen takes
  // whatever the editor pasted — a pair of numbers or a Google Maps link.
  const coords = parseCoords(coordsText);
  const onCoords = (text) => setCoordsText(text);

  const buildPayload = () => {
    /** @type {Record<string, any>} */
    const p = { ...form };
    p.amenities = String(p.amenities || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (coords) {
      p.latitude = coords.lat;
      p.longitude = coords.lng;
    }
    // A beach is a listing like any other: its own fields ride alongside, its
    // category is fixed to the guide and its region is the region field.
    if (p.type === "Beach") {
      p.category = BEACH_CATEGORY;
      Object.assign(p, beachPayload(p));
    }
    return p;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const updated = await base44.entities.Listing.update(listing.id, buildPayload());
      onSaved(updated);
      onClose();
    } catch (e2) {
      setErr(e2.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-[640px] sm:rounded-[20px] rounded-t-[20px] max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#ededed9] shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase font-black text-[#6d6d6a]">Edit listing</div>
            <div className="text-[18px] font-extrabold tracking-[-0.03em] truncate max-w-[420px]">{listing.name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f3f2ee] grid place-items-center" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={save} className="overflow-y-auto px-5 py-5 space-y-2.5">
          <Section title="Basics">
            <Field label="Name">
              <input className={inputCls} value={form.name} onChange={set("name")} required />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select className={inputCls} value={form.type} onChange={set("type")}>
                  {TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={set("status")}>
                  {STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>
            {form.type === "Beach" ? (
              <Field label="Category" hint="Beaches browse as their own guide — the region is set below.">
                <input className={inputCls} value={BEACH_CATEGORY} disabled />
              </Field>
            ) : (
              <>
                <Field label="Category">
                  <select
                    className={inputCls}
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
                  >
                    <option value="">— Select —</option>
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
                {/* Subcategories belong to a category — the list follows the choice above. */}
                <Field
                  label="Subcategory"
                  hint={form.category ? undefined : "Choose a category first — its own subcategories appear here."}
                >
                  <select
                    className={inputCls}
                    value={form.subcategory}
                    onChange={set("subcategory")}
                    disabled={!form.category}
                  >
                    <option value="">{form.category ? "— Select —" : "—"}</option>
                    {subcategoriesFor(form.category).map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </>
            )}
            <Field label="Neighborhood">
              <select className={inputCls} value={form.neighborhood_id} onChange={set("neighborhood_id")}>
                <option value="">— Not set —</option>
                {neighborhoods.map((n) => (
                  <option key={n.id} value={n.slug || n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section title="Descriptions">
            <Field label="Editorial description">
              <textarea className={inputCls} rows={3} value={form.editorial_description} onChange={set("editorial_description")} />
            </Field>
            <Field label="Standard description">
              <textarea className={inputCls} rows={2} value={form.description} onChange={set("description")} />
            </Field>
          </Section>

          {form.type === "Beach" && (
            <Section title="Beach guide">
              <BeachFields form={form} set={(k, v) => setForm({ ...form, [k]: v })} />
            </Section>
          )}

          <Section title="Contact & Visit">
            <Field label="Address">
              <input className={inputCls} value={form.address} onChange={set("address")} />
            </Field>
            <Field
              label="Coordinates"
              hint={
                coords
                  ? `Pinned at ${coords.lat}, ${coords.lng}`
                  : "Paste 18.4655, -66.1180 — or a Google Maps link — to pin this place on the map."
              }
            >
              <input
                className={inputCls}
                value={coordsText}
                onChange={(e) => onCoords(e.target.value)}
                placeholder="18.4655, -66.1180 or a Google Maps link"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input className={inputCls} value={form.phone} onChange={set("phone")} />
              </Field>
              <Field label="Website URL">
                <input className={inputCls} value={form.website_url} onChange={set("website_url")} placeholder="https://" />
              </Field>
            </div>
            <Field label="Languages spoken">
              <input className={inputCls} value={form.language_support} onChange={set("language_support")} placeholder="English, Spanish" />
            </Field>
          </Section>

          {/* The week is the part an editor comes back to most, and it is long
              enough to want a drawer of its own rather than the bottom of
              another one. */}
          <Section title="Hours">
            <HoursEditor
              hours={form.hours_json}
              line={form.hours_text}
              inputCls={inputCls}
              labelCls={labelCls}
              onChange={({ hours, line }) => setForm((p) => ({ ...p, hours_json: hours, hours_text: line }))}
            />
          </Section>

          <Section title="Details">
            <Field label="Amenities / tags" hint="Separate them with commas.">
              <input
                className={inputCls}
                value={form.amenities}
                onChange={set("amenities")}
                placeholder="Outdoor seating, Wi-Fi, Parking"
              />
            </Field>
          </Section>

          <Section title="Connect · Social links (Premium)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="WhatsApp URL">
                <input className={inputCls} value={form.whatsapp_url} onChange={set("whatsapp_url")} placeholder="https://wa.me/…" />
              </Field>
              <Field label="Instagram">
                <input className={inputCls} value={form.instagram_url} onChange={set("instagram_url")} placeholder="https://" />
              </Field>
              <Field label="Facebook">
                <input className={inputCls} value={form.facebook_url} onChange={set("facebook_url")} placeholder="https://" />
              </Field>
              <Field label="TikTok">
                <input className={inputCls} value={form.tiktok_url} onChange={set("tiktok_url")} placeholder="https://" />
              </Field>
              <Field label="X / Twitter">
                <input className={inputCls} value={form.twitter_url} onChange={set("twitter_url")} placeholder="https://" />
              </Field>
              <Field label="YouTube">
                <input className={inputCls} value={form.youtube_url} onChange={set("youtube_url")} placeholder="https://" />
              </Field>
            </div>
          </Section>

          <Section title="Primary action · Premium">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Action type">
                <select className={inputCls} value={form.primary_action_type} onChange={set("primary_action_type")}>
                  {ACTIONS.map((a) => (
                    <option key={a} value={a}>{a || "— None —"}</option>
                  ))}
                </select>
              </Field>
              <Field label="Affiliate provider">
                <select className={inputCls} value={form.affiliate_provider} onChange={set("affiliate_provider")}>
                  {AFFILIATES.map((a) => (
                    <option key={a} value={a}>{a || "— None —"}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Action URL">
              <input className={inputCls} value={form.primary_action_url} onChange={set("primary_action_url")} placeholder="https://" />
            </Field>
            <Field label="Booking URL">
              <input className={inputCls} value={form.booking_url} onChange={set("booking_url")} placeholder="https://" />
            </Field>
          </Section>

          <Section title="Media">
            <p className="mb-3 text-[12px] leading-5 text-[#777773]">
              Choose one strong cover for the main hero. Add different photos to the gallery; the public page automatically removes a duplicate of the hero from the gallery.
            </p>
            <Field label="Front of business / storefront" hint="Used as the fallback hero if no cover is set.">
              <ImageUpload value={form.storefront_image} onChange={(v) => setForm({ ...form, storefront_image: v })} />
            </Field>
            <Field label="Cover image" hint="Shown on the browse cards.">
              <ImageUpload value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} />
            </Field>
            <Field label="Gallery" hint="Premium shows up to six photos; Basic shows one.">
              <ImageUpload multiple value={form.gallery_images} onChange={(v) => setForm({ ...form, gallery_images: v })} />
            </Field>
          </Section>

          <Section title="Tier">
            <Field
              label="Plan"
              hint="Premium carries the verified badge, the full gallery, the social links and the booking buttons."
            >
              <select className={inputCls} value={form.plan} onChange={set("plan")}>
                {PLANS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </Field>
            <Toggle label="Verified badge" checked={form.verified} onChange={setChk("verified")} />
          </Section>

          <Section title="Editorial flags">
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <Toggle label="Featured" checked={form.featured} onChange={setChk("featured")} />
              <Toggle label="Editor pick" checked={form.editor_pick} onChange={setChk("editor_pick")} />
              <Toggle label="Locals know" checked={form.locals_know} onChange={setChk("locals_know")} />
            </div>
          </Section>

          {err && <div className="text-[13px] font-bold text-red-600">{err}</div>}
        </form>

        <div className="flex gap-2 px-5 py-4 border-t border-[#ededed9] shrink-0 bg-[#fafaf7] rounded-b-[20px]">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#deded9] bg-white px-4 py-3 text-[14px] font-bold">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 rounded-full bg-[#171717] text-white px-4 py-3 text-[14px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}